import { Tabs, TabsContent, TabsList, TabsTrigger } from '@atoms/Tabs'
import Icon from '@components/utils/Icon'
import { scopedTranslation } from '@utils/I18n'

import { SupporterDetailsTab, type SupporterDetailsType } from './tabs/SupporterDetailsTab'
import ContactsLog from '../features/show-supporter/ContactsLog'
import NotesLog from '../features/show-supporter/NotesLog'
import { SmsConversationTab } from '../features/show-supporter/SmsConversationTab'
import type { SmsConversationPage } from '../features/show-supporter/SmsConversation'

import type { SupporterContactsQueryType, SupporterNotesQueryType } from '@frontend/graphql/queries/supporters'
import type { EngagementLevelsIndexQueryType } from '@frontend/graphql/queries/engagementLevels'
import type { TagFiltersIndexQueryType } from '@frontend/graphql/queries/tagFilters'
import type { SharedInertiaProps } from '../types/supporterTypes'
import type { CallingSessionSharedInertiaProps } from '@frontend/contexts/users_management/pages/CallingSessionSupporterShow'

export type SupporterPermissions = SharedInertiaProps['permissions'] | CallingSessionSharedInertiaProps['permissions']

export const SUPPORTER_TABS = {
  details: 'supporterDetails',
  contacts: 'contactsLog',
  sms: 'smsConversation',
  notes: 'notesLog',
} as const

export type SupporterTabValue = (typeof SUPPORTER_TABS)[keyof typeof SUPPORTER_TABS]

const t = scopedTranslation('supporter_show')

type SupporterTabsProps = {
  supporter: SupporterDetailsType
  contacts: SupporterContactsQueryType['contacts']
  supporterNotes: SupporterNotesQueryType['supporterNotes']
  supporterSmsMessages?: SmsConversationPage
  engagementLevels: EngagementLevelsIndexQueryType['engagementLevels']
  tagFilters?: TagFiltersIndexQueryType['tagFilters']
  supporterTagFilters?: Array<{ value: string; label: string }>
  permissions: Partial<SupporterPermissions>
  groupId: string
  defaultTab?: SupporterTabValue
}

export function SupporterTabs({
  supporter,
  contacts,
  supporterNotes,
  supporterSmsMessages,
  engagementLevels,
  tagFilters,
  supporterTagFilters,
  permissions,
  groupId,
  defaultTab = SUPPORTER_TABS.details,
}: SupporterTabsProps) {
  return (
    <Tabs defaultValue={defaultTab} className="tw:flex tw:flex-col tw:gap-4">
      <TabsList className="tw:-m-1 tw:w-fit tw:max-w-[calc(100%_+_0.5rem)] tw:justify-start tw:overflow-x-auto tw:p-1">
        <TabsTrigger value={SUPPORTER_TABS.details}>
          <Icon type="user-square" className="tw:mr-2" width={20} height={20} />
          {t('supporter_details')}
        </TabsTrigger>
        <TabsTrigger value={SUPPORTER_TABS.contacts}>
          <Icon type="mail-01" className="tw:mr-2" width={20} height={20} />
          {t('contacts_log')}
        </TabsTrigger>
        {permissions.smsInboxEnabled && supporterSmsMessages && (
          <TabsTrigger value={SUPPORTER_TABS.sms}>
            <Icon type="message-chat-square" className="tw:mr-2" width={20} height={20} />
            {t('sms_conversation.tab')}
          </TabsTrigger>
        )}
        <TabsTrigger value={SUPPORTER_TABS.notes}>
          <Icon type="book-open-01" className="tw:mr-2" width={20} height={20} />
          {t('notes')}
        </TabsTrigger>
      </TabsList>

      <TabsContent value={SUPPORTER_TABS.details}>
        <SupporterDetailsTab
          supporter={supporter}
          engagementLevels={engagementLevels!}
          tagFilters={tagFilters}
          supporterTagFilters={supporterTagFilters}
          permissions={permissions}
          groupId={groupId}
        />
      </TabsContent>

      <TabsContent value={SUPPORTER_TABS.contacts}>
        <ContactsLog contacts={contacts!.nodes || []} totalPages={contacts!.totalPages || 0} />
      </TabsContent>

      {permissions.smsInboxEnabled && supporterSmsMessages && (
        <TabsContent value={SUPPORTER_TABS.sms}>
          <SmsConversationTab
            supporterId={supporter.id}
            groupId={groupId}
            supporterSmsMessages={supporterSmsMessages}
          />
        </TabsContent>
      )}

      <TabsContent value={SUPPORTER_TABS.notes}>
        <NotesLog notes={supporterNotes!.nodes || []} supporterId={supporter.id} groupId={groupId} />
      </TabsContent>
    </Tabs>
  )
}

export type { SupporterDetailsType }
